'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnalysisCommentIndicator } from '@/components/features/resume/AnalysisCommentIndicator';
import type { ResumeAnalysisSuggestion } from '@/lib/resume/analysis';

/**
 * Overlay markers on the preview side, next to whichever section an analysis
 * suggestion targets.
 *
 * The templates stamp `data-rf="<path>"` on the element behind each editable
 * field (see `renderer.ts` and `attachInlinePreviewEditing.ts` — same
 * attribute the click-to-edit system already reads). This maps a suggestion's
 * `target` to that same path so the two features share one source of truth
 * for "which element is this."
 */
function targetToDataPath(target: ResumeAnalysisSuggestion['target']): string | null {
  switch (target.type) {
    case 'summary':
      return 'profile.summary';
    case 'employment':
      return `employment.${target.id}.responsibilities`;
    case 'customSection':
      return `customSections.${target.id}.content`;
    default:
      // 'manual' targets have no inline anchor.
      return null;
  }
}

/** Quote a data-rf value for use inside an attribute selector. */
function attrSelector(path: string): string {
  return `[data-rf="${path.replace(/["\\]/g, '\\$&')}"]`;
}

interface PositionedGroup {
  path: string;
  suggestions: ResumeAnalysisSuggestion[];
  top: number;
}

interface PreviewAnnotationsProps {
  suggestions: ResumeAnalysisSuggestion[];
  showComments: boolean;
  onApply: (suggestion: ResumeAnalysisSuggestion) => void;
  applyingSuggestionId?: string | null;
  /** The relatively-positioned wrapper the markers are placed inside. */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Finds each suggestion's target element inside the preview iframe(s) and
 * renders a small `AnalysisCommentIndicator` dot at its position, on the left
 * edge of the preview container.
 *
 * The preview renders every page as its own same-origin `srcDoc` iframe (see
 * `ResumeDocumentPreview`), and — because each iframe carries the SAME full
 * document, merely clipped and shifted to show only its own page's slice — a
 * `data-rf` match turns up in every iframe even though it is genuinely
 * visible in only one. A match is only accepted when the element's vertical
 * centre actually lands inside THAT iframe's own visible clip box; every
 * other hit is the identical, clipped-away copy that really belongs to a
 * different page. When no iframe's clip box contains it, the suggestion is
 * left unpositioned rather than guessed at.
 */
export function PreviewAnnotations({
  suggestions,
  showComments,
  onApply,
  applyingSuggestionId,
  containerRef,
}: PreviewAnnotationsProps) {
  const [groups, setGroups] = useState<PositionedGroup[]>([]);
  const rafRef = useRef<number | null>(null);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container || !showComments || suggestions.length === 0) {
      setGroups((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const byPath = new Map<string, ResumeAnalysisSuggestion[]>();
    for (const suggestion of suggestions) {
      const path = targetToDataPath(suggestion.target);
      if (!path) continue;
      const arr = byPath.get(path) ?? [];
      arr.push(suggestion);
      byPath.set(path, arr);
    }

    if (byPath.size === 0) {
      setGroups((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const iframes = Array.from(container.querySelectorAll('iframe'));
    const next: PositionedGroup[] = [];

    for (const [path, group] of byPath) {
      const selector = attrSelector(path);
      let top: number | null = null;

      for (const iframe of iframes) {
        const doc = iframe.contentDocument;
        if (!doc) continue;

        let element: Element | null = null;
        try {
          element = doc.querySelector(selector);
        } catch {
          element = null;
        }
        if (!element) continue;

        // The clip box is the iframe's immediate parent: `PreviewPage` wraps
        // each iframe in an `overflow-hidden` div sized to that page's
        // visible window (see ResumeDocumentPreview.tsx).
        const clipBox = iframe.parentElement ?? iframe;
        const iframeRect = iframe.getBoundingClientRect();
        const clipRect = clipBox.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        // Ratio between the iframe's on-screen (scaled) size and its
        // unscaled layout size — the scale lives on an ancestor of the
        // iframe, not the iframe itself, so this recovers it the same way
        // `ResumeDocumentPreview`'s own focusField does.
        const scaleRatio = iframe.offsetWidth > 0 ? iframeRect.width / iframe.offsetWidth : 1;
        const screenTop = iframeRect.top + elementRect.top * scaleRatio;
        const screenBottom = screenTop + elementRect.height * scaleRatio;
        const screenCentre = (screenTop + screenBottom) / 2;

        if (screenCentre >= clipRect.top - 1 && screenCentre <= clipRect.bottom + 1) {
          top = screenTop - containerRect.top;
          break;
        }
      }

      if (top !== null) {
        next.push({ path, suggestions: group, top });
      }
    }

    next.sort((a, b) => a.top - b.top);

    setGroups((prev) => {
      if (
        prev.length === next.length &&
        prev.every((p, i) => p.path === next[i].path && Math.abs(p.top - next[i].top) < 0.5)
      ) {
        return prev;
      }
      return next;
    });
  }, [containerRef, suggestions, showComments]);

  const scheduleRecalculate = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      recalculate();
    });
  }, [recalculate]);

  useEffect(() => {
    // Deferred (not called synchronously in the effect body) so this never
    // triggers a same-commit cascading render.
    scheduleRecalculate();

    const container = containerRef.current;
    window.addEventListener('resize', scheduleRecalculate);
    // 'scroll' does not bubble, but it does propagate through the capture
    // phase, and the actual scrollable viewport lives several layers deep
    // inside `ResumeDocumentPreview` — this catches it without reaching in.
    container?.addEventListener('scroll', scheduleRecalculate, true);

    let resizeObserver: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleRecalculate);
      resizeObserver.observe(container);
    }

    // The iframe(s) reload on every content edit or autosave, zoom eases over
    // ~220ms, and fonts can finish loading after first paint — a light poll
    // catches all of that without wiring into each of those internals.
    const settleTimer = window.setTimeout(recalculate, 500);
    const interval = window.setInterval(recalculate, 1500);

    return () => {
      window.removeEventListener('resize', scheduleRecalculate);
      container?.removeEventListener('scroll', scheduleRecalculate, true);
      resizeObserver?.disconnect();
      window.clearTimeout(settleTimer);
      window.clearInterval(interval);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [recalculate, scheduleRecalculate, containerRef]);

  if (!showComments || groups.length === 0) return null;

  return (
    <>
      {groups.map((group) => (
        <AnalysisCommentIndicator
          key={group.path}
          suggestions={group.suggestions}
          onApply={onApply}
          isApplying={applyingSuggestionId}
          className="absolute left-1 z-20"
          style={{ top: `${group.top}px` }}
        />
      ))}
    </>
  );
}
